import { useState } from 'react';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { friendlyError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { PollClientPickDialog } from '@/components/polls/PollClientPickDialog';

// ============================================================================
// PollManualProtocolDialog — РУЧНОЙ протокол внешней (авторской) методики:
// у шаблона нет вопросов и авто-скоринга — специалист вносит результат сам
// (балл и/или заключение + дата прохождения). Сохранение — серверный RPC
// poll.submitPollResult в ТУ ЖЕ таблицу протоколов типа {code}, что и
// прохождение: subject_id — клиент (для psy-методики обязателен), entity_id —
// кто внёс результат; «Пройдено» сервер вычисляет только при заданном пороге.
// ============================================================================

// Сегодняшняя дата (локальная) в формате ГГГГ-ММ-ДД — для поля «Дата прохождения».
function todayIso(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function PollManualProtocolDialog({ pollId, pollName, psyMode, subject: fixedSubject, onClose, onSaved }: {
    pollId: string;
    pollName: string;
    psyMode: boolean;
    /** Заранее известный клиент (из карточки пациента) — выбор не требуется. */
    subject?: { id: string; name: string } | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const showError = useToastStore((s) => s.showError);
    const [subject, setSubject] = useState<{ id: string; name: string } | null>(fixedSubject ?? null);
    const [clientPick, setClientPick] = useState(false);
    const [score, setScore] = useState('');
    const [conclusion, setConclusion] = useState('');
    const [date, setDate] = useState(todayIso);
    const [busy, setBusy] = useState(false);

    const save = async () => {
        if (psyMode && !subject?.id) { showError('Выберите клиента — протокол без клиента не создаётся'); return; }
        const scoreNum = score.trim() === '' ? undefined : Number(score);
        if (scoreNum !== undefined && !Number.isFinite(scoreNum)) { showError('Балл должен быть числом'); return; }
        if (scoreNum === undefined && conclusion.trim() === '') { showError('Укажите балл или заключение'); return; }
        setBusy(true);
        try {
            await getSDK().poll.submitPollResult({
                pollId,
                subjectId: subject?.id || '',
                conclusion: conclusion.trim(),
                date,
                ...(scoreNum !== undefined ? { score: scoreNum } : {}),
            });
            useToastStore.getState().showSuccess('Протокол создан');
            onSaved();
            onClose();
        } catch (e: any) {
            showError(friendlyError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <UniversalWindow open onClose={onClose} title={`Протокол: ${pollName}`} width="w-[min(520px,94vw)]" height="auto" resizable={false}>
            <div className="space-y-3 text-sm">
                <p className="text-xs text-muted-foreground">
                    Внешняя (авторская) методика: контента нет — специалист вносит результат вручную.
                </p>
                {psyMode && (
                    <div className="flex items-center gap-2">
                        <span className="w-24 shrink-0 text-xs text-muted-foreground sm:w-28">Клиент</span>
                        <span className="min-w-0 flex-1 truncate">{subject?.name || 'не выбран'}</span>
                        {!fixedSubject && (
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setClientPick(true)} disabled={busy}>
                                {subject ? 'Сменить' : 'Выбрать'}
                            </Button>
                        )}
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-xs text-muted-foreground sm:w-28">Балл</span>
                    <input type="number" className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm"
                        value={score} onChange={(e) => setScore(e.target.value)} placeholder="необязательно" disabled={busy} />
                </div>
                <div className="flex items-start gap-2">
                    <span className="w-24 shrink-0 pt-1.5 text-xs text-muted-foreground sm:w-28">Заключение</span>
                    <textarea className="min-h-20 min-w-0 flex-1 rounded-md border bg-background px-2 py-1.5 text-sm"
                        value={conclusion} onChange={(e) => setConclusion(e.target.value)}
                        placeholder="необязательно, если указан балл" disabled={busy} />
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-xs text-muted-foreground sm:w-28">Дата прохождения</span>
                    <input type="date" className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm sm:flex-none"
                        value={date} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose} disabled={busy}>Отмена</Button>
                    <Button size="sm" className="h-8 text-xs" onClick={() => void save()} disabled={busy}>
                        {busy ? 'Сохранение…' : 'Сохранить'}
                    </Button>
                </div>
            </div>
            {clientPick && (
                <PollClientPickDialog
                    onSelect={(id, name) => { setClientPick(false); setSubject({ id, name }); }}
                    onClose={() => setClientPick(false)}
                />
            )}
        </UniversalWindow>
    );
}
